import { plainToClass } from 'class-transformer';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { PageFieldAttribute } from '@views/pages/admin/models/page-fields/page-field-attribute.model';

import { Page } from './models/page.model';
import { Category } from './models/category.model';
import { User } from './models/user.model';
import { Role } from './models/role.model';
import { PageType } from './models/page-type.model';

import { environment } from '../../../../environments/environment';
import { httpParams, toFormData } from '../../../core/_utils';

const PAGE_FIELD_ATTRIBUTE = `${environment.apiPrefix}/pages-field-attribute`;
const PAGES_FIELD_VALUES = `${environment.apiPrefix}/pages-field-values`;

const AUTH_URL = `${environment.apiPrefix}/auth`;
const CATEGORIES_API_URL = `${environment.apiPrefix}/categories`;
const PAGES_TYPES_API_URL = `${environment.apiPrefix}/pages-types`;
const FAQ_API_URL = `${environment.apiPrefix}/faq`;
const PAGES_API_URL = `${environment.apiPrefix}/pages`;
const ROLES_API_URL = `${environment.apiPrefix}/roles`;
const USERS_API_URL = `${environment.apiPrefix}/users`;

const SAVE_PAGE_FIELD_VALUES = `${PAGES_FIELD_VALUES}/save`;

const GET_ALL_PAGES_ATTRIBUTES = `${PAGE_FIELD_ATTRIBUTE}/get-all`;
const FILTER_ATTRIBUTES_BY_PAGE_TYPE_ = `${PAGE_FIELD_ATTRIBUTE}/filter-page-type`;

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  constructor(private http: HttpClient) {}

  getPagesList(query: any): Observable<{ pages: Page[]; metadata: any }> {
    return this.http.get<{ pages: Page[]; metadata: any }>(PAGES_API_URL, { params: httpParams(query) });
  }

  getPageById(id: number): Observable<Page> {
    return this.http.get<Page>(`${PAGES_API_URL}/${id}`);
  }

  getPageBySlug(slug: string): Observable<Page> {
    return this.http.get<Page>(PAGES_API_URL, { params: httpParams({ slug }) });
  }

  addPage(data: Page): Observable<Page> {
    return this.http.post<Page>(PAGES_API_URL, data);
  }

  updatePage(id: number, data: Page): Observable<Page> {
    return this.http.put<Page>(`${PAGES_API_URL}/${id}`, data);
  }

  removePage(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${PAGES_API_URL}/${id}`);
  }

  removePages(ids: number[]): Observable<boolean> {
    if (ids.length) {
      return this.http.delete<boolean>(PAGES_API_URL, { params: httpParams({ ids }) });
    }

    return of(true);
  }

  getCategoriesList(query: any = {}): Observable<{ categories: Category[]; metadata: any }> {
    return this.http.get<{ categories: Category[]; metadata: any }>(CATEGORIES_API_URL, { params: httpParams(query) });
  }

  getPagesTypesList(): Observable<PageType[]> {
    return this.http.get<PageType[]>(PAGES_TYPES_API_URL);
  }

  getCategoryById(id: number): Observable<Category> {
    return this.http.get<Category>(`${CATEGORIES_API_URL}/${id}`);
  }

  addCategory(data: Category): Observable<Category> {
    return this.http.post<Category>(CATEGORIES_API_URL, toFormData(data));
  }

  updateCategory(id: number, data: Category): Observable<Category> {
    return this.http.put<Category>(`${CATEGORIES_API_URL}/${id}`, toFormData(data));
  }

  removeCategory(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${CATEGORIES_API_URL}/${id}`);
  }

  removeCategories(ids: number[]): Observable<boolean> {
    if (ids.length) {
      return this.http.delete<boolean>(CATEGORIES_API_URL, { params: httpParams({ ids }) });
    }

    return of(true);
  }

  getUsersList(query: any = {}): Observable<{ users: User[]; metadata: any }> {
    return this.http
      .get<{ users: User[]; metadata: any }>(USERS_API_URL, { params: httpParams(query) })
      .pipe(
        map(({ users, metadata }) => {
          return {
            users: plainToClass(User, users, { excludeExtraneousValues: true }),
            metadata,
          };
        }),
      );
  }

  getUserById(id: number): Observable<User> {
    return this.http
      .get<User>(`${USERS_API_URL}/${id}`)
      .pipe(map((user) => plainToClass(User, user, { excludeExtraneousValues: true })));
  }

  addUser(data: User): Observable<User> {
    return this.http
      .post<User>(USERS_API_URL, data)
      .pipe(map((user) => plainToClass(User, user, { excludeExtraneousValues: true })));
  }

  updateUser(id: number, data: User): Observable<User> {
    return this.http
      .put<User>(`${USERS_API_URL}/${id}`, data)
      .pipe(map((user) => plainToClass(User, user, { excludeExtraneousValues: true })));
  }

  removeUser(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${USERS_API_URL}/${id}`);
  }

  removeUsers(ids: number[]): Observable<boolean> {
    return this.http.delete<boolean>(USERS_API_URL, { params: httpParams({ ids }) });
  }

  getRolesList(query: any = {}): Observable<{ roles: Role[]; metadata: any }> {
    return this.http
      .get<{ roles: Role[]; metadata: any }>(ROLES_API_URL, { params: httpParams(query) })
      .pipe(
        map(({ roles, metadata }) => {
          return {
            roles: plainToClass(Role, roles, { excludeExtraneousValues: true }),
            metadata,
          };
        }),
      );
  }

  getRoleById(id: number): Observable<Role> {
    return this.http
      .get<Role>(`${ROLES_API_URL}/${id}`)
      .pipe(map((role) => plainToClass(Role, role, { excludeExtraneousValues: true })));
  }

  addRole(data: Role): Observable<Role> {
    return this.http
      .post<Role>(ROLES_API_URL, data)
      .pipe(map((role) => plainToClass(Role, role, { excludeExtraneousValues: true })));
  }

  updateRole(id: number, data: Role): Observable<Role> {
    return this.http
      .put<Role>(`${ROLES_API_URL}/${id}`, data)
      .pipe(map((role) => plainToClass(Role, role, { excludeExtraneousValues: true })));
  }

  removeRole(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${ROLES_API_URL}/${id}`);
  }

  removeRoles(ids: number[]): Observable<boolean> {
    return this.http.delete<boolean>(ROLES_API_URL, { params: httpParams({ ids }) });
  }

  loginAs(userId: number): Observable<any> {
    return this.http.post(`${AUTH_URL}/login_as`, { user_id: userId });
  }

  getQuestionList(page: number = null, pageSize: number = null): Observable<{ faq: any[]; metadata: any }> {
    const params = {};

    // tslint:disable no-string-literal
    if (page) {
      params['offset'] = (page - 1) * pageSize;
    }

    if (pageSize) {
      params['limit'] = pageSize;
    }
    // tslint:enable no-string-literal

    return this.http.get<{ faq: any[]; metadata: any }>(FAQ_API_URL, {
      params: httpParams(params),
    });
  }

  getQuestionById(id: number): Observable<any> {
    return this.http.get<any>(`${FAQ_API_URL}/${id}`);
  }

  addQuestion(data: any): Observable<any> {
    return this.http.post<any>(FAQ_API_URL, data);
  }

  editQuestion(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${FAQ_API_URL}/${id}`, data);
  }

  deleteQuestion(id: number): Observable<boolean> {
    return this.http.delete<any>(`${FAQ_API_URL}/${id}`);
  }

  deleteQuestionMany(ids: number[]): Observable<boolean> {
    if (ids.length) {
      return this.http.delete<boolean>(FAQ_API_URL, { params: httpParams({ ids }) });
    }

    return of(true);
  }

  getAllPagesAttribute(): Observable<PageFieldAttribute[]> {
    return this.http.get<PageFieldAttribute[]>(GET_ALL_PAGES_ATTRIBUTES);
  }

  savePageFields(data, pageId) {
    return this.http.post(SAVE_PAGE_FIELD_VALUES, { data, pageId });
  }

  filterAttributesByPageType(pageTypeId): Observable<PageFieldAttribute[]> {
    return this.http.post<PageFieldAttribute[]>(FILTER_ATTRIBUTES_BY_PAGE_TYPE_, { pageTypeId });
  }
}
